import type React from 'react';

import { SITE_LAYOUT } from 'tradera/constants/ui';
import { useSiteLayout } from 'tradera/apps/tradera/components/discover/hooks/use-site-layout';
import { classNames } from 'tradera/utils/class-names';

import styles from './site-width.module.scss';

type SiteWidthProps = React.PropsWithChildren & {
    /**
     * If true, the layout will read the siteLayout from Redux and adapt to the user's preference.
     */
    allowFullWidth?: boolean;
    narrow?: boolean;
    extraNarrow?: boolean;
    /**
     * Adds default padding on the sides
     */
    padded?: boolean;
    className?: string;
};

/**
 * This component will wrap the children in a container with a max-width,
 * based on the SASS variable $site-max-width.
 *
 * If the user has selected full width layout, the children will be wrapped in a container with no max-width.
 */
export const SiteWidth = ({
    children,
    allowFullWidth,
    extraNarrow,
    narrow,
    padded,
    className,
    ...props
}: SiteWidthProps) => {
    const [siteLayout] = useSiteLayout();
    const isFullWidthLayoutFromUserPreferences =
        siteLayout === SITE_LAYOUT.FULL_WIDTH;

    const isFullWidth = allowFullWidth && isFullWidthLayoutFromUserPreferences;

    return (
        <div
            className={classNames(
                styles.wrapper,
                isFullWidth && styles.fullWidth,
                extraNarrow && styles.extraNarrow,
                narrow && styles.narrow,
                padded && 'px-2 px-md-3 px-lg-2',
                padded && !isFullWidth && 'px-xl-1',
                padded && isFullWidth && 'px-xl-6',
                className
            )}
            {...props}>
            {children}
        </div>
    );
};
